import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, UploadCloud, Sparkles, Trash2, CheckCircle2, AlertCircle, FileText } from "lucide-react";
import { apiFetch, uploadFile } from "@/portal/api";
import { toast } from "@/components/ui/sonner";

const CATS = [
  { key: "service", label: "Servis/opravy" },
  { key: "insurance", label: "Pojištění" },
  { key: "fuel", label: "Palivo" },
  { key: "purchase", label: "Nákup vozu" },
  { key: "marketing", label: "Marketing" },
  { key: "other", label: "Ostatní" },
];
const today = () => new Date().toISOString().slice(0, 10);
const CONCURRENCY = 4;

// Run async worker over items with limited concurrency.
async function pool(items, worker, limit) {
  let i = 0;
  const run = async () => { while (i < items.length) { const idx = i++; await worker(items[idx], idx); } };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
}

const BulkExpenseModal = ({ cars = [], token, onClose, onDone }) => {
  const [rows, setRows] = useState([]); // {file, status, amount, date, category, supplier, note, car_id, error}
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [rateCache, setRateCache] = useState({}); // { "YYYY-MM-DD": { date, rates } }
  const fileRef = useRef(null);

  // Load ČNB rates for every (date) used by a foreign-currency row.
  useEffect(() => {
    const needed = [...new Set(rows.filter((r) => r.currency && r.currency !== "CZK" && r.date).map((r) => r.date))]
      .filter((d) => rateCache[d] === undefined);
    if (!needed.length) return;
    setRateCache((prev) => { const n = { ...prev }; needed.forEach((d) => { n[d] = null; }); return n; }); // mark loading
    needed.forEach((d) => {
      apiFetch(`/admin/fx/rates?date=${encodeURIComponent(d)}`, { token })
        .then((res) => setRateCache((prev) => ({ ...prev, [d]: res })))
        .catch(() => setRateCache((prev) => ({ ...prev, [d]: { date: d, rates: {} } })));
    });
  }, [rows, rateCache, token]);

  const czkFor = (r) => {
    const amt = Number(r.amount);
    if (!amt || amt <= 0) return null;
    if (!r.currency || r.currency === "CZK") return { czk: amt, rate: null };
    const rc = rateCache[r.date];
    const rate = rc?.rates?.[r.currency];
    if (!rate) return null;
    return { czk: Math.round(amt * rate), rate, fxDate: rc.date };
  };

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 60);
    if (!files.length) return;
    const base = files.map((f) => ({ file: f, status: "reading", amount: "", currency: "CZK", date: today(), category: "other", supplier: "", note: "", car_id: "", error: null }));
    setRows((prev) => [...prev, ...base]);
    const startIdx = rows.length;
    setReading(true);
    await pool(files, async (f, k) => {
      const idx = startIdx + k;
      try {
        const r = await uploadFile("/admin/expenses/ocr", f, token);
        setRows((prev) => prev.map((row, j) => j === idx ? {
          ...row, status: "done",
          amount: r.amount != null ? String(r.amount) : "",
          currency: r.currency || "CZK",
          date: r.date || today(), category: r.category || "other",
          supplier: r.supplier || "", note: r.note || "",
        } : row));
      } catch (e) {
        setRows((prev) => prev.map((row, j) => j === idx ? { ...row, status: "error", error: e.message } : row));
      }
    }, CONCURRENCY);
    setReading(false);
  };

  const upd = (idx, patch) => setRows((prev) => prev.map((r, j) => j === idx ? { ...r, ...patch } : r));
  const removeRow = (idx) => setRows((prev) => prev.filter((_, j) => j !== idx));

  const valid = rows.filter((r) => r.status !== "error" && Number(r.amount) > 0);

  const saveAll = async () => {
    if (!valid.length) { toast.error("Není co uložit – zkontrolujte částky."); return; }
    setSaving(true); setSavedCount(0);
    let ok = 0, fail = 0;
    for (const r of valid) {
      try {
        const body = { date: r.date || today(), category: r.category, amount: Number(r.amount), currency: r.currency || "CZK", note: r.note || null, car_id: r.car_id || null, supplier: r.supplier || null };
        const created = await apiFetch("/admin/expenses", { method: "POST", token, body });
        if (created?.id) { try { await uploadFile(`/admin/expenses/${created.id}/attachment`, r.file, token); } catch (e) { /* keep expense even if attach fails */ } }
        ok += 1; setSavedCount(ok);
      } catch (e) { fail += 1; }
    }
    setSaving(false);
    toast.success(`Uloženo ${ok} nákladů${fail ? `, ${fail} se nepodařilo` : ""}.`);
    onDone();
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="bulk-expense-modal">
      <div className="glass rounded-2xl w-full max-w-4xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-400" /> Hromadné nahrání účtenek (AI)</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="bulk-expense-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-white/15 rounded-2xl p-6 text-center cursor-pointer hover:border-orange-500/50 transition-colors mb-4"
            data-testid="bulk-expense-dropzone"
          >
            <UploadCloud className="w-8 h-8 text-orange-400 mx-auto mb-2" />
            <p className="text-sm text-zinc-300">Přetáhni sem účtenky nebo klikni pro výběr</p>
            <p className="text-[11px] text-zinc-500 mt-1">Klidně 50 najednou · fotky i PDF · AI z nich přečte částku, datum a dodavatele</p>
            <input ref={fileRef} type="file" multiple accept=".pdf,image/*" className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="bulk-expense-file-input" />
          </div>

          {rows.length > 0 && (
            <div className="space-y-2" data-testid="bulk-expense-rows">
              <div className="hidden sm:grid grid-cols-[1.4fr_0.9fr_1.1fr_1.3fr_1.6fr_auto] gap-2 text-[10px] uppercase tracking-wider text-zinc-500 px-1">
                <span>Doklad</span><span>Částka / měna</span><span>Datum</span><span>Kategorie</span><span>Dodavatel / poznámka</span><span></span>
              </div>
              {rows.map((r, idx) => (
                <div key={idx} className="grid grid-cols-2 sm:grid-cols-[1.4fr_0.9fr_1.1fr_1.3fr_1.6fr_auto] gap-2 items-center bg-white/5 rounded-xl p-2" data-testid={`bulk-row-${idx}`}>
                  <div className="flex items-center gap-1.5 min-w-0 col-span-2 sm:col-span-1">
                    {r.status === "reading" ? <Loader2 className="w-4 h-4 text-orange-400 animate-spin shrink-0" /> : r.status === "error" ? <AlertCircle className="w-4 h-4 text-red-400 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                    <span className="text-xs text-zinc-300 truncate" title={r.file.name}>{r.file.name}</span>
                  </div>
                  {r.status === "error" ? (
                    <div className="col-span-2 sm:col-span-4 text-[11px] text-red-400">{r.error || "Nepodařilo se přečíst"}</div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex gap-1">
                          <input type="number" value={r.amount} onChange={(e) => upd(idx, { amount: e.target.value })} placeholder="0" className="input-dark !py-1.5 text-sm flex-1 min-w-0" data-testid={`bulk-amount-${idx}`} />
                          <select value={r.currency} onChange={(e) => upd(idx, { currency: e.target.value })} className="input-dark !py-1.5 !px-1 text-xs w-14" data-testid={`bulk-currency-${idx}`}>
                            <option value="CZK" className="bg-[#0a0a0a]">Kč</option>
                            <option value="EUR" className="bg-[#0a0a0a]">€</option>
                            <option value="USD" className="bg-[#0a0a0a]">$</option>
                          </select>
                        </div>
                        {r.currency !== "CZK" && Number(r.amount) > 0 && (() => {
                          const c = czkFor(r);
                          return c
                            ? <span className="text-[10px] text-emerald-300 leading-tight" data-testid={`bulk-czk-${idx}`}>≈ {c.czk.toLocaleString("cs-CZ")} Kč · kurz {c.rate}{c.fxDate ? ` (${c.fxDate})` : ""}</span>
                            : <span className="text-[10px] text-zinc-500 leading-tight">přepočet…</span>;
                        })()}
                      </div>
                      <input type="date" value={r.date} onChange={(e) => upd(idx, { date: e.target.value })} className="input-dark !py-1.5 text-sm" data-testid={`bulk-date-${idx}`} />
                      <select value={r.category} onChange={(e) => upd(idx, { category: e.target.value })} className="input-dark !py-1.5 text-sm" data-testid={`bulk-cat-${idx}`}>
                        {CATS.map((c) => <option key={c.key} value={c.key} className="bg-[#0a0a0a]">{c.label}</option>)}
                      </select>
                      <div className="flex flex-col gap-1">
                        <input value={r.supplier} onChange={(e) => upd(idx, { supplier: e.target.value })} placeholder="Dodavatel" className="input-dark !py-1.5 text-sm" data-testid={`bulk-supplier-${idx}`} />
                        <input value={r.note} onChange={(e) => upd(idx, { note: e.target.value })} placeholder="Poznámka" className="input-dark !py-1.5 text-xs" data-testid={`bulk-note-${idx}`} />
                      </div>
                    </>
                  )}
                  <button onClick={() => removeRow(idx)} className="text-zinc-600 hover:text-red-400 p-1 justify-self-end" data-testid={`bulk-remove-${idx}`} aria-label="Odebrat"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-white/10">
          <span className="text-xs text-zinc-400">{reading ? "AI čte účtenky…" : rows.length ? `${valid.length} připraveno k uložení z ${rows.length}${(() => { const t = valid.reduce((s, r) => { const c = czkFor(r); return s + (c ? c.czk : 0); }, 0); return t ? ` · ≈ ${t.toLocaleString("cs-CZ")} Kč` : ""; })()}` : "Zatím nic nenahráno"}</span>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-sm text-zinc-400 hover:text-white" data-testid="bulk-expense-cancel">Zavřít</button>
            <button onClick={saveAll} disabled={saving || reading || !valid.length} className="btn-accent inline-flex items-center gap-2 disabled:opacity-60" data-testid="bulk-expense-save">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Ukládám {savedCount}/{valid.length}</> : <><CheckCircle2 className="w-4 h-4" /> Uložit vše ({valid.length})</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkExpenseModal;
